import React from 'react';
import PropTypes from 'prop-types';
import { FILTER_IDS } from '@src/js/constants.js';
import { useSearchParams } from 'react-router-dom';
import ApiUtils from '@js/api-utils.es6.js';
import ScheduleProviderCard from '@src/components/scheduling-provider-card/ScheduleProviderCard.jsx';
import ScheduleProviderCardUtils from '@src/js/provider-card-utils.js';
import { useDependenciesContext } from '@src/contexts/dependencies.jsx';
import FindAProviderResultsContentType from '@js/content-types/find-a-provider-results-content-type.es6.js';

const MIN_LOCATIONS = 2;

/**
 * Renders a list of provider cards for the Find a Provider Results page.
 */
const ProviderResultsCards = ({ providers }) => {
  const [searchParams,] = useSearchParams();
  const location = searchParams.get(FILTER_IDS.LOCATION);

  const { geolocationService, searchApiService } = useDependenciesContext();

  const findAProviderResults = new FindAProviderResultsContentType();
  const scheduleAnAppointmentUrl = findAProviderResults.scheduleAnAppointmentLink.anchor.$(0).attr('href');
  const scheduleAnAppointmentLabel = findAProviderResults.scheduleAnAppointmentLink.anchor.$(0).text();
  const hideLocationText = findAProviderResults.hideLocationsLabel.item.$(0).text();
  const milesLabel = findAProviderResults.milesLabel.item.$(0).text();
  const awayLabel = findAProviderResults.awayLabel.item.$(0).text();
  const callLabel = findAProviderResults.callLabel.item.$(0).text();
  const viewMoreStartingText = findAProviderResults.showMoreLocationsStart.item.$(0).text();
  const viewMoreEndingText = findAProviderResults.showMoreLocationsEnd.item.$(0).text();
  const viewMoreEndingTextSingular = findAProviderResults.showMoreLocationsSingle.item.$(0).text();

  return (
    <div className='providers-results-card--container'>
      {
        providers.map(provider => {
          const npi = provider.field_provider_npi_number;
          const isAffiliate = !searchApiService.convertBooleanStringToRealBoolean(provider.field_is_luminis_health_provider);
          const isAcceptingNewPatients = searchApiService.convertBooleanStringToRealBoolean(provider.field_accepting_new_patients);
          const isAcceptingTelehealth = searchApiService.convertBooleanStringToRealBoolean(provider.field_offering_telehealth);

          const alias = ApiUtils.buildProviderAlias(provider.title);
          const image = ScheduleProviderCardUtils.getProviderImage(provider);
          const primaryLocation = provider.primary_location;
          const locationsWithoutPrimary = provider.locations.filter(location => location.field_epic_office_id !== primaryLocation?.field_epic_office_id);
          const otherLocations = locationsWithoutPrimary.slice(0, MIN_LOCATIONS - 1);
          const remainingLocations = locationsWithoutPrimary.slice(MIN_LOCATIONS - 1, locationsWithoutPrimary.length);

          return (
            <ScheduleProviderCard
              key={provider.title}
              affiliateProviderLabel={provider.field_affiliated_provider_label}
              alias={alias}
              hideLocationText={hideLocationText}
              image={image}
              initialLocation={primaryLocation}
              isAffiliate={isAffiliate}
              luminisProviderLabel={provider.field_is_luminis_health_provider_label}
              name={provider.field_hero_banner_text_1}
              noRatingsText={provider.field_no_rating_text}
              noRatingsLink={provider.field_no_rating_link}
              npi={npi}
              otherLocations={otherLocations}
              ratingsLabel={provider.field_ratings_label}
              remainingLocations={remainingLocations}
              specialties={ScheduleProviderCardUtils.getProviderSpecialtiesArray(provider)}
              userLocation={location}
              geolocationService={geolocationService}
              milesLabel={milesLabel}
              awayLabel={awayLabel}
              callLabel={callLabel}
              affiliateProviderTooltip={provider.field_affiliated_provider_tooltip}
              luminisProviderTooltip={provider.field_luminis_health_provider_to}
              viewMoreStartingText={viewMoreStartingText}
              viewMoreEndingText={viewMoreEndingText}
              viewMoreEndingTextSingular={viewMoreEndingTextSingular}
              showSchedulingOptions={true}
              isAcceptingNewPatients={isAcceptingNewPatients}
              acceptingNewPatientsLabel={provider.field_accepting_new_patients_lab}
              isAcceptingTelehealth={isAcceptingTelehealth}
              isAcceptingTelehealthLabel={provider.field_offering_telehealth_label}
              hasOnlineAvailability={provider.hasOnlineAvailability}
              hasInPersonOnlineAvailability={provider.hasInPersonOnlineAvailability}
              hasTelemedicineOnlineAvailability={provider.hasTelemedicineOnlineAvailability}
              scheduleAnAppointmentLabel={scheduleAnAppointmentLabel}
              scheduleAnAppointmentPath={scheduleAnAppointmentUrl}
            />
          );
        })
      }
    </div>
  );
}

ProviderResultsCards.propTypes = {
  /**
   * an array of provider results from the Search API
   */
  providers: PropTypes.array.isRequired,
};

export default ProviderResultsCards;
